import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Menu,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { shadow } from '../theme/tokens';
import { useProject } from '../context/ProjectContext';
import { drugService } from '../services/drugService';
import { getPinnedStudies, pinStudy, unpinStudy } from '../services/pinnedStudies.service';
import PageHeader from './common/PageHeader';
import EmptyState from './common/EmptyState';
import LoadingState from './common/LoadingState';
import StatusBadge from './common/StatusBadge';
import {
  projectListPrimaryLabel,
  projectListSecondaryLabel,
  resolveProjectSponsorName,
  resolveProjectSponsorProtocolId,
  resolveProjectTrialAcronym,
} from '../utils/projectIdentityFields';
import {
  OPEN_MODE_OPTIONS,
  OPEN_MODE_DESIGN_STUDIO,
  getCachedStudyOpenMode,
  setCachedStudyOpenMode,
  clearCachedStudyOpenMode,
} from '../utils/studyOpenPreference';
import studyOpenPreferenceService from '../services/studyOpenPreference.service';

/**
 * The caption line under a study's title shows the drug (parent project) name —
 * distinct, useful context. It used to also repeat the Sponsor Protocol
 * Identifier as a prefix, but projectListPrimaryLabel() always uses that same
 * identifier as the title itself whenever one is set, so that prefix was
 * 100% redundant with the title above it, not just an edge case — dropped
 * entirely. Separately, if a study has no local Project extension row yet,
 * its title can fall back to the same underlying IAM resource name as its
 * parent drug (see projectIdentityFields.js); this still guards that case by
 * suppressing the drug name when it equals the title.
 */
function drugNameForCaption(study, drug) {
  const drugName = drug?.name ? String(drug.name).trim() : '';
  if (!drugName) return '';
  const primary = String(projectListPrimaryLabel(study) || '').trim();
  return drugName.toLowerCase() === primary.toLowerCase() ? '' : drugName;
}

/** One study card, reused by both the Pinned section and the per-drug grid below. */
const StudyCard = ({ study, drug, isSelected, isPinned, pinBusy, onOpen, onTogglePin, onResetOpenMode }) => {
  const secondary = projectListSecondaryLabel(study);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = !!menuAnchor;

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = (e) => {
    e?.stopPropagation();
    setMenuAnchor(null);
  };
  const handleResetOpenModeClick = (e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    onResetOpenMode(study);
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderColor: isSelected ? 'primary.main' : undefined,
        backgroundColor: isSelected ? 'rgba(30, 115, 190, 0.04)' : undefined,
        boxShadow: isSelected ? '0 0 0 1px rgba(30, 115, 190, 0.35)' : undefined,
        '&:hover': {
          boxShadow: shadow.md,
          borderColor: isSelected ? 'primary.main' : 'text.disabled',
        },
      }}
      onClick={onOpen}
    >
      <Tooltip title="Study options">
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{ position: 'absolute', top: 4, right: 32, zIndex: 1 }}
        >
          <MoreVertIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleResetOpenModeClick}>Reset Open Mode</MenuItem>
      </Menu>
      <Tooltip title={isPinned ? 'Unpin study' : 'Pin study to top of Home'}>
        <IconButton
          size="small"
          disabled={pinBusy}
          onClick={onTogglePin}
          sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
        >
          {isPinned
            ? <PushPinIcon fontSize="small" sx={{ color: 'primary.main' }} />
            : <PushPinOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
        </IconButton>
      </Tooltip>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ minWidth: 0, pr: 3 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, wordBreak: 'break-word' }}
          >
            {drugNameForCaption(study, drug)}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: isSelected ? 700 : 600, color: isSelected ? 'primary.main' : 'text.primary', wordBreak: 'break-word', mt: 0.25 }}
          >
            {projectListPrimaryLabel(study)}
          </Typography>
        </Box>

        {secondary ? (
          <Box sx={{ mt: 1.5 }}>
            <Chip
              label={secondary}
              size="small"
              variant="outlined"
              sx={{ maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          </Box>
        ) : null}

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          {study.status ? (
            <StatusBadge status={study.status} />
          ) : <Box />}
          <Button
            size="small"
            endIcon={<ArrowForwardIcon fontSize="small" />}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            sx={{ ml: 'auto' }}
          >
            Open
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

/** One study row, list-view equivalent of StudyCard — same data/actions, compact single line. */
const StudyListRow = ({ study, drug, isSelected, isPinned, pinBusy, onOpen, onTogglePin, onResetOpenMode }) => {
  const secondary = projectListSecondaryLabel(study);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = !!menuAnchor;

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = (e) => {
    e?.stopPropagation();
    setMenuAnchor(null);
  };
  const handleResetOpenModeClick = (e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    onResetOpenMode(study);
  };

  return (
    <Box
      onClick={onOpen}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        backgroundColor: isSelected ? 'rgba(30, 115, 190, 0.04)' : 'background.paper',
        '&:hover': { boxShadow: shadow.md, borderColor: isSelected ? 'primary.main' : 'text.disabled' },
      }}
    >
      <Tooltip title={isPinned ? 'Unpin study' : 'Pin study to top of Home'}>
        <IconButton size="small" disabled={pinBusy} onClick={onTogglePin}>
          {isPinned
            ? <PushPinIcon fontSize="small" sx={{ color: 'primary.main' }} />
            : <PushPinOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
        </IconButton>
      </Tooltip>

      <Box sx={{ minWidth: 0, flex: '1 1 40%' }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: isSelected ? 700 : 600, color: isSelected ? 'primary.main' : 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {projectListPrimaryLabel(study)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {drugNameForCaption(study, drug)}
        </Typography>
      </Box>

      {secondary ? (
        <Chip
          label={secondary}
          size="small"
          variant="outlined"
          sx={{ display: { xs: 'none', md: 'inline-flex' }, maxWidth: 260, flexShrink: 0 }}
        />
      ) : null}

      <Box sx={{ flex: '0 0 auto', minWidth: 90 }}>
        {study.status ? <StatusBadge status={study.status} /> : null}
      </Box>

      <Button
        size="small"
        endIcon={<ArrowForwardIcon fontSize="small" />}
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        sx={{ flexShrink: 0 }}
      >
        Open
      </Button>

      <Tooltip title="Study options">
        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreVertIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
        <MenuItem onClick={handleResetOpenModeClick}>Reset Open Mode</MenuItem>
      </Menu>
    </Box>
  );
};

const HOME_VIEW_MODE_STORAGE_KEY = 'homeViewMode';

/**
 * Home — landing page. Projects (Drug docs) as section headers, each project's
 * Studies (Project docs) as a card grid underneath. Read-only: projects and studies
 * are sourced from IAM/Hub sync — this page browses and opens them, it does not
 * create, edit, or delete either.
 */
const Home = () => {
  const navigate = useNavigate();
  const { currentProject, loadProject } = useProject();

  const [drugs, setDrugs] = useState([]);
  const [studiesByDrug, setStudiesByDrug] = useState({});
  const [loadingDrugs, setLoadingDrugs] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');

  // Card vs. list view — a per-browser display preference, not project data,
  // so it's read from/written to localStorage rather than the server.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = window.localStorage.getItem(HOME_VIEW_MODE_STORAGE_KEY);
      return saved === 'list' ? 'list' : 'card';
    } catch (_) {
      return 'card';
    }
  });
  const handleViewModeChange = (_e, next) => {
    if (!next) return;
    setViewMode(next);
    try {
      window.localStorage.setItem(HOME_VIEW_MODE_STORAGE_KEY, next);
    } catch (_) {}
  };

  // Pinned study ids, most-recently-pinned first (server is the source of
  // truth — see pinnedStudies.service.js). pinBusy tracks in-flight toggles
  // so a card's pin button can't be double-clicked mid-request.
  const [pinnedIds, setPinnedIds] = useState([]);
  const [pinBusy, setPinBusy] = useState({});

  useEffect(() => {
    fetchOverview();
    getPinnedStudies()
      .then(setPinnedIds)
      .catch((err) => console.error('Home: failed to load pinned studies:', err));
  }, []);

  const handleTogglePin = useCallback(async (e, study) => {
    e.stopPropagation();
    const id = study._id;
    if (pinBusy[id]) return;
    const isPinned = pinnedIds.includes(id);
    setPinBusy((prev) => ({ ...prev, [id]: true }));
    // Optimistic update — pin/unpin should feel instant; reconciled with the
    // server's list (which also reorders by most-recently-pinned) below.
    setPinnedIds((prev) => (isPinned ? prev.filter((pid) => pid !== id) : [id, ...prev]));
    try {
      const next = isPinned ? await unpinStudy(id) : await pinStudy(id);
      setPinnedIds(next);
    } catch (err) {
      console.error('Home: failed to toggle pin for study', id, err);
      setPinnedIds((prev) => (isPinned ? [id, ...prev] : prev.filter((pid) => pid !== id)));
    } finally {
      setPinBusy((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, [pinnedIds, pinBusy]);

  const fetchOverview = async () => {
    setLoadingDrugs(true);
    setLoadError(null);
    try {
      const { drugs, studiesByDrug } = await drugService.getHomeOverview();
      setDrugs(drugs);
      setStudiesByDrug(studiesByDrug || {});
    } catch (err) {
      // Previously swallowed entirely — a real 401/403/500 here rendered the
      // exact same "No projects yet" empty state as a genuinely-empty
      // account, with no way to tell the two apart. Surface it instead.
      console.error('Home: failed to load project/study overview:', err?.response?.status, err?.response?.data || err?.message);
      setDrugs([]);
      setStudiesByDrug({});
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load projects. Please try again.'
      );
    }
    setLoadingDrugs(false);
  };

  // Which mode a study opens in is a project/study-level setting, shared by every
  // user — asked once (by whoever opens the study first) and remembered from then
  // on for everyone. Opening a study with no saved preference yet parks it here
  // until the dialog is answered.
  const [openModePrompt, setOpenModePrompt] = useState(null); // { study }
  const [pendingOpenMode, setPendingOpenMode] = useState(OPEN_MODE_DESIGN_STUDIO);

  const openStudy = useCallback(async (study) => {
    await loadProject(study._id);
    navigate(`/clinical-intake?project=${study._id}&tab=protocol`);
  }, [loadProject, navigate]);

  const handleStudyCardClick = async (study) => {
    // The DB is the source of truth; the cache exists only so a repeat open
    // doesn't have to wait on a network round trip or get blocked if the API is
    // briefly unreachable — it must never be allowed to override the DB when
    // both are available. So the DB is always checked first; the cache is
    // consulted only as a fallback if that check fails, and is corrected
    // whenever it disagrees with what the DB returns.
    let dbMode = null;
    let dbCheckFailed = false;
    try {
      dbMode = await studyOpenPreferenceService.getPreference(study._id);
    } catch (err) {
      dbCheckFailed = true;
      console.error('Home: failed to load study open preference for', study._id, err);
    }

    if (dbMode) {
      // Authoritative value from the DB — refresh the cache if it was stale
      // (or empty) and open. No dialog: a saved preference already exists.
      setCachedStudyOpenMode(study._id, dbMode);
      await openStudy(study);
      return;
    }

    if (dbCheckFailed) {
      // DB unreachable — fall back to whatever is cached locally so the study
      // can still open; if nothing is cached either, fall through to the
      // dialog below rather than blocking the user entirely.
      const cachedMode = getCachedStudyOpenMode(study._id);
      if (cachedMode) {
        await openStudy(study);
        return;
      }
    }

    // No preference saved for this study yet (or the DB confirmed none exists) —
    // ask once. Whatever is chosen here applies to every user who opens this
    // study from now on.
    setPendingOpenMode(OPEN_MODE_DESIGN_STUDIO);
    setOpenModePrompt({ study });
  };

  const handleOpenModeContinue = async () => {
    const study = openModePrompt?.study;
    if (!study) return;
    const requestedMode = pendingOpenMode;
    setOpenModePrompt(null);
    // Default to the user's own pick if the save fails outright (see catch below);
    // when the save succeeds, `mode` is overwritten with the authoritative value
    // the backend actually stored — which may not be `requestedMode` if another
    // user's selection for this same project won a race (create-once: the
    // backend never overwrites an existing preference).
    let mode = requestedMode;
    try {
      const result = await studyOpenPreferenceService.savePreference(study._id, requestedMode);
      mode = result.openMode;
      if (!result.applied) {
        // Someone else set this project's preference first (possibly just now,
        // in a concurrent request) — proceed with their choice instead of ours.
        console.info(
          'Home: study open preference for', study._id,
          'was already set to', mode, '- proceeding with the existing choice.'
        );
      }
    } catch (err) {
      // DB save failed — still honor the user's choice for this session via the
      // cache rather than blocking them from opening the study; other users (and
      // this one, on another browser) will simply see the dialog again until a
      // save succeeds.
      console.error('Home: failed to save study open preference for', study._id, err);
    }
    setCachedStudyOpenMode(study._id, mode);
    await openStudy(study);
  };

  // Reset Open Mode (3-dot menu on a study card): clears this one project's
  // shared preference in the DB so the next Open shows the chooser dialog
  // again, for anyone. Scoped strictly to the selected study — no other
  // study's preference, pin state, or data is touched.
  const [resetOpenModePrompt, setResetOpenModePrompt] = useState(null); // { study }
  const [resetOpenModeBusy, setResetOpenModeBusy] = useState(false);
  const [resetOpenModeError, setResetOpenModeError] = useState('');

  const handleResetOpenModeRequest = (study) => {
    setResetOpenModeError('');
    setResetOpenModePrompt({ study });
  };

  const handleResetOpenModeConfirm = async () => {
    const study = resetOpenModePrompt?.study;
    if (!study) return;
    setResetOpenModeBusy(true);
    setResetOpenModeError('');
    try {
      await studyOpenPreferenceService.resetPreference(study._id);
      // Clear the local cache too — otherwise this browser could still fall
      // back to the old mode from cache the next time the DB request fails,
      // even though the DB itself was already reset.
      clearCachedStudyOpenMode(study._id);
      setResetOpenModePrompt(null);
    } catch (err) {
      console.error('Home: failed to reset study open preference for', study._id, err);
      setResetOpenModeError('Failed to reset the open mode. Please try again.');
    }
    setResetOpenModeBusy(false);
  };

  const totalProjects = drugs.length;
  const totalStudies = useMemo(
    () => Object.values(studiesByDrug).reduce((sum, list) => sum + (list?.length || 0), 0),
    [studiesByDrug]
  );

  // Study id -> { study, drug }, so the Pinned section (which spans drugs)
  // can render a card without re-deriving which drug each study belongs to.
  const studyLookup = useMemo(() => {
    const map = {};
    drugs.forEach((drug) => {
      (studiesByDrug[drug._id] || []).forEach((study) => {
        map[study._id] = { study, drug };
      });
    });
    return map;
  }, [drugs, studiesByDrug]);

  const pinnedStudies = pinnedIds
    .map((id) => studyLookup[id])
    .filter(Boolean);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = (study) => {
    if (!normalizedSearch) return true;
    const haystack = [
      resolveProjectTrialAcronym(study),
      resolveProjectSponsorName(study),
      resolveProjectSponsorProtocolId(study),
    ].join(' ').toLowerCase();
    return haystack.includes(normalizedSearch);
  };

  const visibleDrugs = drugs.filter((drug) => projectFilter === 'all' || drug._id === projectFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Projects"
        subtitle="Browse your programs and open a study to continue authoring."
      />

      {loadingDrugs ? (
        <LoadingState label="Loading projects…" />
      ) : loadError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchOverview}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : drugs.length === 0 ? (
        <EmptyState
          icon={<FolderOpenOutlinedIcon fontSize="medium" />}
          title="No projects yet"
          description="Projects synced from IAM will show up here."
        />
      ) : (
        <>
          {/* Summary stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Total Studies
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {totalStudies}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Total Projects
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {totalProjects}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Search + project filter */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
            <TextField
              size="small"
              placeholder="Search by title, identifier or sponsor"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 240, maxWidth: 420 }}
            />
            <TextField
              select
              size="small"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Projects</MenuItem>
              {drugs.map((drug) => (
                <MenuItem key={drug._id} value={drug._id}>{drug.name}</MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ ml: 'auto' }}
            >
              <ToggleButton value="card" aria-label="Card view">
                <Tooltip title="Card view">
                  <GridViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view">
                <Tooltip title="List view">
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {pinnedStudies.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PushPinIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pinned Studies</Typography>
              </Box>
              {viewMode === 'list' ? (
                <Stack spacing={1}>
                  {pinnedStudies
                    .filter(({ study }) => matchesSearch(study))
                    .map(({ study, drug }) => (
                      <StudyListRow
                        key={study._id}
                        study={study}
                        drug={drug}
                        isSelected={currentProject && currentProject._id === study._id}
                        isPinned
                        pinBusy={!!pinBusy[study._id]}
                        onOpen={() => handleStudyCardClick(study)}
                        onTogglePin={(e) => handleTogglePin(e, study)}
                        onResetOpenMode={handleResetOpenModeRequest}
                      />
                    ))}
                </Stack>
              ) : (
                <Grid container spacing={2}>
                  {pinnedStudies
                    .filter(({ study }) => matchesSearch(study))
                    .map(({ study, drug }) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={study._id}>
                        <StudyCard
                          study={study}
                          drug={drug}
                          isSelected={currentProject && currentProject._id === study._id}
                          isPinned
                          pinBusy={!!pinBusy[study._id]}
                          onOpen={() => handleStudyCardClick(study)}
                          onTogglePin={(e) => handleTogglePin(e, study)}
                          onResetOpenMode={handleResetOpenModeRequest}
                        />
                      </Grid>
                    ))}
                </Grid>
              )}
              <Divider sx={{ mt: 3 }} />
            </Box>
          )}

          {visibleDrugs.map((drug, drugIndex) => {
            const studiesForDrug = (studiesByDrug[drug._id] || []).filter(matchesSearch);
            if (normalizedSearch && studiesForDrug.length === 0) return null;

            return (
            <Box key={drug._id} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={drug.name}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'rgba(30, 115, 190, 0.08)',
                      color: 'primary.dark',
                      border: '1px solid rgba(30, 115, 190, 0.25)',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {studiesForDrug.length} {studiesForDrug.length === 1 ? 'study' : 'studies'}
                  </Typography>
                </Box>
              </Box>

              {studiesForDrug.length === 0 ? (
                <EmptyState
                  compact
                  icon={<LocalHospitalIcon fontSize="medium" />}
                  title="No studies yet"
                  description="Studies synced from IAM for this project will show up here."
                />
              ) : viewMode === 'list' ? (
                <Stack spacing={1}>
                  {studiesForDrug.map(study => (
                    <StudyListRow
                      key={study._id}
                      study={study}
                      drug={drug}
                      isSelected={currentProject && currentProject._id === study._id}
                      isPinned={pinnedIds.includes(study._id)}
                      pinBusy={!!pinBusy[study._id]}
                      onOpen={() => handleStudyCardClick(study)}
                      onTogglePin={(e) => handleTogglePin(e, study)}
                      onResetOpenMode={handleResetOpenModeRequest}
                    />
                  ))}
                </Stack>
              ) : (
                <Grid container spacing={2}>
                  {studiesForDrug.map(study => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={study._id}>
                      <StudyCard
                        study={study}
                        drug={drug}
                        isSelected={currentProject && currentProject._id === study._id}
                        isPinned={pinnedIds.includes(study._id)}
                        pinBusy={!!pinBusy[study._id]}
                        onOpen={() => handleStudyCardClick(study)}
                        onTogglePin={(e) => handleTogglePin(e, study)}
                        onResetOpenMode={handleResetOpenModeRequest}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {drugIndex < visibleDrugs.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
            );
          })}
        </>
      )}

      <Dialog
        open={!!openModePrompt}
        onClose={() => setOpenModePrompt(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          How do you want to open this study?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {projectListPrimaryLabel(openModePrompt?.study || {})} — choose once; this
            study will open the same way next time.
          </Typography>
          <RadioGroup
            value={pendingOpenMode}
            onChange={(e) => setPendingOpenMode(e.target.value)}
          >
            {OPEN_MODE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenModePrompt(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleOpenModeContinue}
            disabled={!pendingOpenMode}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!resetOpenModePrompt}
        onClose={() => (resetOpenModeBusy ? null : setResetOpenModePrompt(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Reset the open mode for this study?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {projectListPrimaryLabel(resetOpenModePrompt?.study || {})} will lose its saved
            "open with" choice. The next time anyone opens this study, they will be asked
            to choose Design Studio or Document Authoring Tool again — only this study is
            affected.
          </Typography>
          {resetOpenModeError ? (
            <Alert severity="error" sx={{ mt: 2 }}>{resetOpenModeError}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetOpenModePrompt(null)} disabled={resetOpenModeBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleResetOpenModeConfirm}
            disabled={resetOpenModeBusy}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Home;
